import {
  ConstrainMode,
  IColumn,
  IDetailsRowProps,
  IImageProps,
  IObjectWithKey,
  IPlainCardProps,
  TooltipHost,
} from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPImage,
  IPImageFit,
  IPLink,
  IPScrollablePane,
  IPShimmeredDetailsList,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPHoverCard, IPHoverCardType } from 'baseComponents/HoverCard/IPHoverCard';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { onRenderIntegrationContext } from 'components/Integrations/IntegrationRenderHelpers';
import HrefLink from 'components/Links/HrefLink';
import { RecurringPatternSummaryStyle, RecurringSummary } from 'components/RecurringPattern/RecurringSummary';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { canUpdateTaskField, hasTaskUpdatePermission, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackTokensGapExtraSmall } from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Entity from 'models/entity';
import { Integration, IntegrationStates } from 'models/integrations/integration';
import { IntegrationRun } from 'models/integrations/integrationRun';
import { InternalAuditSchedule, InternalAuditScheduleOccurrence } from 'models/internalAuditTemplate';
import RecurringPattern from 'models/recurringPattern';
import { AuditYearStart } from 'models/setting';
import Tag from 'models/tag';
import { default as Task, TaskTypes } from 'models/tasks/task';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import Config from 'services/Config/configService';
import { rotateArrayLeft } from 'utils/array';
import { getAuditStartForSetting } from 'utils/datetime';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { sortOnDate, sortOnNumber, sortOnString, sortOnTaskTags } from 'utils/sorting';
import { AuditPeriodSummary } from '../../Audit/AnnualPlan/AuditPeriodSummary';
import { IntegrationRunPeriodSummary } from '../../Integrations/AnnualPlan/IntegrationRunPeriodSummary';
import { FilterTaskGroupKeys } from '../Filter/FilterTaskGroupKeys';
import { TaskDateLinkWithHoverCard } from './TaskDateLinkWithHoverCard';
import { TaskOverflowLinkWithHoverCard } from './TaskOverflowLinkWithHoverCard';
import { onRenderTaskContext } from './TaskRenderHelpers';
import { TaskViewScheduleLevel } from './TasksView';

type ItemType = Task | Integration | InternalAuditSchedule;

//audit series and integration rows share this tint, so both stand apart from the plain task rows
const annualPlanRowTintLight = '#fff6ec';
const annualPlanRowTintDark = '#4e443a';

interface ITaskScheduleDetailsList {
  level: TaskViewScheduleLevel;
  year: number;
  tasksDisplay: Task[];
  integrations?: Integration[];
  integrationsDisplay?: Integration[];
  auditSchedulesDisplay?: InternalAuditSchedule[];
  newCopilotTasks?: Task[];
  isUpdating: boolean;
  isLoading: boolean;
  hideContext?: boolean;
  hidePeriodColumns?: boolean;
  filter: string[];
  selectionMode?: IPSelectionMode;
  allowEditSeries?: boolean;
  cacheKeySort: LocalStorageKeys;
  onChangeSelection?: (selectedTasks: Task[]) => void;
  onTaskClick: (task: Task) => void;
  onTaskEditRecurringPattern?: (task: Task) => void;
  onIntegrationClick?: (integration: Integration) => void;
  onIntegrationEditRecurrencePattern?: (integration: Integration) => void;
  onAuditClick?: (schedule: InternalAuditSchedule) => void;
  onAuditOccurrenceClick?: (occurrence: InternalAuditScheduleOccurrence) => void;
  onAuditEditRecurrencePattern?: (schedule: InternalAuditSchedule) => void;
  updateFilter?: (filter: string[]) => void;
  onUpdateTaskSeries?: (series: Task[]) => void;
  onUpdateRun?: (run: IntegrationRun) => void;
}

//
// Static helpers
//
const getPlanningRotation = (startMonth: number, level: TaskViewScheduleLevel): number => {
  if (level === TaskViewScheduleLevel.Month) {
    return startMonth;
  } else if (level === TaskViewScheduleLevel.Quarter) {
    return Math.floor(startMonth / level);
  } else {
    return 0;
  }
};

//
// Component
//
export const TaskScheduleDetailsList = (props: ITaskScheduleDetailsList) => {
  //load common translation files upfront for context
  const { t } = useTranslation([
    'tasks',
    'control',
    'controls',
    'dateTimeComponent',
    'task',
    'asset',
    'audit',
    'risk',
    'control',
    'theme',
    'objective',
    'process',
    'integrations',
    'translation',
    'integrations',
    'kpi',
  ]);

  //
  // States and context
  //
  const appContext = useContext(AppContext);
  const [seriesTasks, setSeriesTasks] = useState<Task[]>([]);
  const [instanceTasks, setInstanceTasks] = useState<Task[]>([]);
  const [itemsDisplay, setItemsDisplay] = useState<ItemType[]>([]);
  const [sortKey, setSortKey] = useState<string>('level');
  const [sortDesc, setSortDesc] = useState<boolean>(false);

  //
  // Constants
  //
  const evidenceCollectionImageProps: IImageProps = {
    src: `${Config.getImageURL()}/evidence-collection.png`,
    imageFit: IPImageFit.contain,
    width: 26,
    height: 26,
  };

  const auditImageProps: IImageProps = {
    src: `${Config.getImageURL()}/audit.png`,
    imageFit: IPImageFit.contain,
    width: 26,
    height: 26,
  };

  const [monthsNames] = useState<string[]>([
    t('dateTimeComponent:MonthsShort.Jan'),
    t('dateTimeComponent:MonthsShort.Feb'),
    t('dateTimeComponent:MonthsShort.Mar'),
    t('dateTimeComponent:MonthsShort.Apr'),
    t('dateTimeComponent:MonthsShort.May'),
    t('dateTimeComponent:MonthsShort.Jun'),
    t('dateTimeComponent:MonthsShort.Jul'),
    t('dateTimeComponent:MonthsShort.Aug'),
    t('dateTimeComponent:MonthsShort.Sep'),
    t('dateTimeComponent:MonthsShort.Oct'),
    t('dateTimeComponent:MonthsShort.Nov'),
    t('dateTimeComponent:MonthsShort.Dec'),
  ]);

  //
  // Memos
  //
  const auditDateSetting: string = useMemo(
    () => appContext.globalDataCache.settings.get(AuditYearStart),
    [appContext.globalDataCache.settings],
  );
  const startMonth: number = useMemo(
    () => getAuditStartForSetting(props.year, auditDateSetting).getMonth(),
    [auditDateSetting, props.year],
  );
  const planningRotation: number = useMemo(
    () => getPlanningRotation(startMonth, props.level),
    [props.level, startMonth],
  );

  //
  // Selection
  //
  const selection = useMemo(() => {
    const newSelection = new IPSelection<ItemType>({
      canSelectItem: (item: ItemType) => {
        if (item instanceof InternalAuditSchedule || item instanceof Integration) {
          return false;
        }

        return !item.completed;
      },
      getKey: (item: ItemType) => {
        if (item instanceof InternalAuditSchedule) {
          return `a-${item.template.internalAuditTemplateId}`;
        } else if (item instanceof Integration) {
          return `i-${item.integrationId}`;
        } else {
          return `t-${item.taskId}`;
        }
      },
      selectionMode: props.selectionMode,
      onSelectionChanged: () => {
        if (props.onChangeSelection) {
          const items = newSelection.getSelection();
          props.onChangeSelection(items.filter((i) => i instanceof Task) as Task[]);
        }
      },
    });

    return newSelection;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onChangeSelection, props.selectionMode]);

  //
  // Effects
  //
  useEffect(() => {
    //
    // When tasks or view options change, split tasks in series and instances
    //
    const newSeriesTasks = getTaskForRows(props.tasksDisplay);
    const newInstanceTasks = getTaskForColumns(props.tasksDisplay);

    setSeriesTasks(newSeriesTasks);
    setInstanceTasks(newInstanceTasks);

    // notify parent about updated series tasks
    if (props.onUpdateTaskSeries) props.onUpdateTaskSeries(newSeriesTasks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tasksDisplay, props.filter, props.year, props.level, props.hideContext, props.isLoading, props.isUpdating]);

  useEffect(() => {
    //
    // when seriesTasks, instanceTasks or integrationsDisplay change, update the itemsDisplay with sorting
    //

    // Build columns to update the otherwise stale state in the column render methods
    const newSort = loadSortFromLocalStorage();
    const newSortKey = newSort[0];
    const newSortDesc = newSort[1] === '1';
    setSortKey(newSortKey);
    setSortDesc(newSortDesc);

    const newColumns = getColumns(
      props.filter,
      props.year,
      props.level,
      props.isLoading,
      newSortKey,
      newSortDesc,
      props.hideContext,
    );

    setColumns(newColumns);

    // Combine series tasks, integrations and audit series to display
    const combinedItems: ItemType[] = [...seriesTasks];
    if (props.integrationsDisplay && props.integrationsDisplay.length > 0) {
      combinedItems.push(...props.integrationsDisplay);
    }
    if (props.auditSchedulesDisplay && props.auditSchedulesDisplay.length > 0) {
      combinedItems.push(...props.auditSchedulesDisplay);
    }

    // Sort items for display
    const sortedItems = copyAndSort(combinedItems, instanceTasks, newSortKey, newSortDesc);
    setItemsDisplay(sortedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesTasks, instanceTasks, props.integrationsDisplay, props.auditSchedulesDisplay]);

  useEffect(() => {
    selection.setItems(itemsDisplay, false);
  }, [itemsDisplay, selection]);

  //
  // Tasks
  //
  const getTaskForRows = (tasks: Task[]): Task[] => {
    //return the series tasks and non-recurring tasks
    return tasks.filter((t) => t.isNonRecurring() || t.isSeries());
  };

  const getTaskForColumns = (tasks: Task[]): Task[] => {
    //return the instance tasks and non-recurring tasks
    return tasks.filter((t) => t.isNonRecurring() || t.isInstance());
  };

  //
  // Helpers
  //
  const onTaskEditRecurringPattern = (item: Task) => {
    if (props.onTaskEditRecurringPattern) {
      props.onTaskEditRecurringPattern(item);
    }
  };

  const onIntegrationEditRecurrencePattern = (item: Integration) => {
    if (props.onIntegrationEditRecurrencePattern) {
      props.onIntegrationEditRecurrencePattern(item);
    }
  };

  const onAuditEditRecurrencePattern = (item: InternalAuditSchedule) => {
    if (props.onAuditEditRecurrencePattern) {
      props.onAuditEditRecurrencePattern(item);
    }
  };

  const setTagFilter = (tag: Tag, selectedFilter: string[]) => {
    let newFilter = [...selectedFilter];
    const filterId = FilterTaskGroupKeys.tag + tag.tagId;
    if (newFilter.indexOf(filterId) >= 0) {
      newFilter = newFilter.filter((f) => f !== filterId);
    } else {
      newFilter.push(filterId);
    }

    if (props.updateFilter) {
      props.updateFilter(newFilter);
    }
  };

  //
  // Columns
  //
  const getColumns = (
    filter: string[],
    year: number,
    level: TaskViewScheduleLevel,
    isLoading: boolean,
    sortKey: string,
    sortDesc: boolean,
    hideContext?: boolean,
  ): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'assignee',
        name: '@',
        minWidth: 28,
        maxWidth: 28,
        isSorted: sortKey === 'assignee',
        isSortedDescending: sortKey === 'assignee' ? sortDesc : false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (!item) return;

          if (item instanceof InternalAuditSchedule) {
            return (
              <TooltipHost content={t('audit:AnnualPlan.RowTag')}>
                <IPImage {...auditImageProps} />
              </TooltipHost>
            );
          } else if (item instanceof Integration) {
            return (
              <TooltipHost
                content={
                  item.externalCheck ? t('integrations:ExternalCheck.Name') : t('integrations:StoreItem.TypeTagText')
                }
              >
                <IPImage {...evidenceCollectionImageProps} />
              </TooltipHost>
            );
          } else {
            const name = item.user?.name;
            const id = item.userId;

            return (
              <IPTooltipHost content={name}>
                <IPPersona
                  imageUrl={appContext.globalDataCache.users.getPhotoURLObj(id)}
                  text={name}
                  size={IPPersonaSize.size24}
                  hidePersonaDetails={true}
                />
              </IPTooltipHost>
            );
          }
        },
      },
      {
        key: 'name',
        name: t('tasks:TaskView.Schedule.Columns.TaskName'),
        minWidth: 200,
        maxWidth: 400,
        isMultiline: true,
        isResizable: true,
        isSorted: sortKey === 'name',
        isSortedDescending: sortKey === 'name' ? sortDesc : false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (!item) return;

          if (item instanceof InternalAuditSchedule) {
            if (props.onAuditClick) {
              return (
                <IPText>
                  <IPLink
                    underline
                    onClick={() => {
                      if (props.onAuditClick) props.onAuditClick(item);
                    }}
                  >
                    {item.template.name}
                  </IPLink>
                </IPText>
              );
            } else {
              return <IPText>{item.template.name}</IPText>;
            }
          } else if (item instanceof Integration) {
            //a SkillControl check: the link opens the same overview as a built-in integration; the modal shows the check
            //read-only, SkillControl owns its schedule and runs (phase 20R2). Disabled is the organization's own switch:
            //SkillControl keeps running the check, this organization records nothing
            if (item.externalCheck) {
              return (
                <IPText>
                  {props.onIntegrationClick ? (
                    <IPLink
                      underline
                      onClick={() => {
                        if (props.onIntegrationClick) props.onIntegrationClick(item);
                      }}
                    >
                      {item.kpi.name}
                    </IPLink>
                  ) : (
                    item.kpi.name
                  )}
                  {item.state === IntegrationStates.Disabled && ' (' + t('integrations:State.Disabled') + ')'}
                  {item.externalCheck.stale && ' (' + t('integrations:ExternalCheck.Stale') + ')'}
                </IPText>
              );
            } else if (props.onIntegrationClick) {
              return (
                <IPText>
                  <IPLink
                    underline
                    onClick={() => {
                      if (props.onIntegrationClick) props.onIntegrationClick(item);
                    }}
                  >
                    {item.kpi.name}
                  </IPLink>
                </IPText>
              );
            } else {
              return <IPText>{item.kpi.name}</IPText>;
            }
          } else {
            let seriesElement: JSX.Element;
            if (item.taskType === TaskTypes.Monitoring) {
              //For monitoring tasks, the series is only editable in the Annual Plan itself (props.allowEditSeries = true)
              //When the user is not allowed to edit the series, the task name is shown non-clickable
              //When the task is completed, the task name is shown with an inactive state
              if (props.allowEditSeries && !item.completed) {
                seriesElement = (
                  <IPText>
                    <IPLink
                      underline
                      onClick={() => {
                        props.onTaskClick(item);
                      }}
                    >
                      {item.name}
                    </IPLink>
                  </IPText>
                );
              } else if (item.completed) {
                let name = item.name;
                if (item.completed) {
                  name = name + ' (' + t('tasks:TaskView.Schedule.InactiveState') + ')';
                }
                seriesElement = <IPText>{name}</IPText>;
              } else {
                seriesElement = <IPText>{item.name}</IPText>;
              }
            } else {
              //For other task types, the series is always clickable and sub-tasks are shown in the month columns
              //When the task is not recurring, the task name is not clickable because the task itself is shown in the month columns
              if (item.taskMasterId === 0) {
                seriesElement = (
                  <IPText>
                    <IPLink
                      underline
                      onClick={() => {
                        props.onTaskClick(item);
                      }}
                    >
                      {item.name}
                    </IPLink>
                  </IPText>
                );
              } else {
                seriesElement = <IPText>{item.name}</IPText>;
              }
            }

            return seriesElement;
          }
        },
      },
      {
        key: 'tags',
        name: t('tasks:TaskView.Schedule.Columns.Tags'),
        minWidth: 80,
        maxWidth: 200,
        isResizable: true,
        isSorted: sortKey === 'tags',
        isSortedDescending: sortKey === 'tags' ? sortDesc : false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (!item) return;
          if (item instanceof InternalAuditSchedule) return null;

          let tagIds: number[] = [];
          if (item instanceof Integration) {
            tagIds = item.kpi.tagIds;
          } else {
            tagIds = item.tagIds ?? [];
          }

          return (
            <KeyValueTags
              compact
              tags={appContext.globalDataCache.tags.getItemsForId(tagIds)}
              onClick={(tag) => setTagFilter(tag, filter)}
            />
          );
        },
      },
      {
        key: 'recurringPattern',
        name: t('tasks:TaskView.Schedule.Columns.RecurringPattern'),
        minWidth: 170,
        maxWidth: 170,
        isMultiline: true,
        isResizable: true,

        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (!item) return;

          if (item instanceof InternalAuditSchedule) {
            if (!item.template.recurrencePattern.isActive) return null;

            //the auth schema of the audit template decides whether the series recurrence can be changed
            const canUpdateAudit = hasUserDataPermission(
              appContext,
              [item.template.authSchemaId],
              AuthSchemaLineOperation.Update,
            );

            return !props.onAuditEditRecurrencePattern || !canUpdateAudit ? (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.text}
                pattern={item.template.recurrencePattern}
                variant="small"
              />
            ) : (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.button}
                pattern={item.template.recurrencePattern}
                onClick={() => onAuditEditRecurrencePattern(item)}
                buttonStyles={{ root: { width: 130 } }}
              />
            );
          } else if (item instanceof Integration) {
            if (item.externalCheck) {
              return <IPText variant="small">{item.externalCheck.schedules.map((s) => s.name).join(', ')}</IPText>;
            }

            return !props.onIntegrationEditRecurrencePattern || item.state !== IntegrationStates.Enabled ? (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.text}
                pattern={item.recurrencePattern}
                variant="small"
              />
            ) : (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.button}
                pattern={item.recurrencePattern}
                onClick={() => onIntegrationEditRecurrencePattern(item)}
                buttonStyles={{ root: { width: 130 } }}
              />
            );
          } else {
            const canUpdateRecurrence = canUpdateTaskField(
              item,
              hasTaskUpdatePermission(item, appContext),
              TaskFieldTypes.Recurrence,
              appContext,
            );

            return !props.onTaskEditRecurringPattern || item.completed || !canUpdateRecurrence ? (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.text}
                pattern={item.recurringPattern}
                variant="small"
              />
            ) : (
              <RecurringSummary
                summaryStyle={RecurringPatternSummaryStyle.button}
                pattern={item.recurringPattern}
                onClick={() => onTaskEditRecurringPattern(item)}
                buttonStyles={{ root: { width: 130 } }}
              />
            );
          }
        },
      },
      {
        key: 'context',
        name: t('tasks:TaskView.Schedule.Columns.Context'),
        minWidth: hideContext ? 1 : 100,
        maxWidth: hideContext ? 1 : 100,
        isMultiline: true,
        isResizable: true,
        isCollapsible: false,
        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (!item || hideContext) return;
          if (item instanceof InternalAuditSchedule) return null;
          let entityCount: number;
          const entity: Entity | undefined = undefined;

          if (item instanceof Integration) {
            entityCount = item.kpi.controlIds?.length || 0;
            entityCount += item.kpi.objectiveIds?.length || 0;
            if (entityCount > 0) {
              const plainCardProps: IPlainCardProps = {
                onRenderPlainCard: onRenderIntegrationContext,
                renderData: item,
              };

              return (
                <IPHoverCard type={IPHoverCardType.plain} plainCardProps={plainCardProps} instantOpenOnClick={true}>
                  <IPText variant="small">
                    <HrefLink entity={entity} underline>
                      {t('translation:General.List.ItemCount', { count: entityCount })}
                    </HrefLink>
                  </IPText>
                </IPHoverCard>
              );
            } else {
              return null;
            }
          } else {
            entityCount = item.riskIds.length;
            entityCount += item.controlIds.length;
            entityCount += item.processIds.length;
            entityCount += item.objectiveIds.length;
            entityCount += item.assetIds.length;
            entityCount += item.processingActivityIds.length;
            entityCount += item.taskRelations?.length ?? 0;

            if (entityCount > 0) {
              const plainCardProps: IPlainCardProps = {
                onRenderPlainCard: onRenderTaskContext,
                renderData: item,
              };

              return (
                <IPHoverCard type={IPHoverCardType.plain} plainCardProps={plainCardProps} instantOpenOnClick={true}>
                  <IPText variant="small">
                    <HrefLink entity={entity} underline>
                      {t('translation:General.List.ItemCount', { count: entityCount })}
                    </HrefLink>
                  </IPText>
                </IPHoverCard>
              );
            } else {
              return null;
            }
          }
        },
      },
    ];

    if (!isLoading && !props.hidePeriodColumns) {
      //render all periode columns
      const periodColumns: IColumn[] = getPeriodColumns(level, year);
      allColumns.push(...periodColumns);
    }

    return allColumns;
  };

  //
  // Period columns render
  // within callback with all dependend helper functions
  //
  const getPeriodColumns = useCallback(
    (level: number, year: number): IColumn[] => {
      //
      // Helper to determine if a date is within the period
      //
      const isInPeriod = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: TaskViewScheduleLevel,
        date: Date,
      ): boolean => {
        const lowerLevelMonth = (yearOrQuarterOrMonthNumber - 1) * level;
        const upperLevelMonth = yearOrQuarterOrMonthNumber * level;

        if (lowerLevelMonth === planningRotation * level) {
          return date.getFullYear() === year && date.getMonth() >= lowerLevelMonth && date.getMonth() < upperLevelMonth;
        } else {
          return date.getMonth() >= lowerLevelMonth && date.getMonth() < upperLevelMonth;
        }
      };

      //
      // Helper to get tasks for the period
      //
      const getTasksForPeriod = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: number,
        task: Task,
      ): Task[] => {
        //when the input task is non-recurring, return that when it's in the period
        if (task.isNonRecurring() && isInPeriod(year, yearOrQuarterOrMonthNumber, level, task.startDateTime)) {
          return [task];
        }

        //get the serie instances in the period
        return instanceTasks
          .filter((_task) => {
            return (
              _task.taskMasterId === task.taskId &&
              isInPeriod(year, yearOrQuarterOrMonthNumber, level, _task.startDateTime)
            );
          })
          .sort((a, b) => sortOnDate(a.startDateTime, b.startDateTime));
      };

      //
      // Helper to get runs for the period
      //
      const getRunsForPeriod = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: number,
        integration: Integration,
      ): IntegrationRun[] => {
        const runs = integration.runs.filter((r) => {
          return isInPeriod(year, yearOrQuarterOrMonthNumber, level, r.dueAt);
        });

        return runs.sort((a, b) => sortOnDate(a.dueAt, b.dueAt));
      };

      //
      // Helper for the calendar date range of a period column. The columns are rotated to the
      // audit year: periods before the rotation boundary belong to the next calendar year.
      //
      const getPeriodRange = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: number,
      ): { start: Date; end: Date } => {
        const lowerMonth = (yearOrQuarterOrMonthNumber - 1) * level;
        const calendarYear = lowerMonth >= planningRotation * level ? year : year + 1;

        return {
          start: new Date(calendarYear, lowerMonth, 1),
          end: new Date(calendarYear, lowerMonth + level, 1),
        };
      };

      //
      // Helper to get audit occurrences that touch the period. An occurrence spans start to end,
      // so overlap counts: the chips across consecutive periods form the bar.
      //
      const getAuditOccurrencesForPeriod = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: number,
        schedule: InternalAuditSchedule,
      ): InternalAuditScheduleOccurrence[] => {
        const { start, end } = getPeriodRange(year, yearOrQuarterOrMonthNumber, level);

        return schedule.occurrences
          .filter((o) => {
            const occurrenceStart = o.startDate;
            if (!occurrenceStart) return false;
            const occurrenceEnd = o.endDate ?? occurrenceStart;

            return occurrenceStart < end && occurrenceEnd >= start;
          })
          .sort((a, b) => sortOnDate(a.startDate, b.startDate));
      };

      const getAuditCalculatedDatesForPeriod = (
        year: number,
        yearOrQuarterOrMonthNumber: number,
        level: number,
        schedule: InternalAuditSchedule,
      ): Date[] => {
        const { start, end } = getPeriodRange(year, yearOrQuarterOrMonthNumber, level);

        return schedule.calculatedDates.filter((d) => d >= start && d < end).sort((a, b) => sortOnDate(a, b));
      };

      //
      // Helper to get the period column name
      //
      const getPeriodColumnName = (year: number, level: TaskViewScheduleLevel, rotation: number): string => {
        switch (level) {
          case TaskViewScheduleLevel.Month:
            return monthsNames[rotation];
          case TaskViewScheduleLevel.Quarter:
            return t('tasks:TaskView.Schedule.Columns.Quarter', { quarter: rotation + 1 });
          case TaskViewScheduleLevel.Year:
            return t('tasks:TaskView.Schedule.Columns.Year', { year: year });
        }
      };

      //
      // Render period column Task
      //
      const renderPeriodColumnTask = (year: number, level: TaskViewScheduleLevel, i: number, item?: Task) => {
        if (!item) return;
        const tasks = getTasksForPeriod(year, i + 1, level, item);
        if (tasks.length === 0) return;
        const hasOverflow = tasks.length > 4;

        return (
          <IPStack tokens={globalStackTokensGapExtraSmall}>
            {tasks.slice(0, 4 - (hasOverflow ? 1 : 0)).map((task: Task) => {
              return (
                <TaskDateLinkWithHoverCard
                  key={'TaskDateLinkWithHoverCard' + task.taskId.toString()}
                  task={task}
                  level={level}
                  onTaskClick={props.onTaskClick}
                />
              );
            })}
            {hasOverflow && <TaskOverflowLinkWithHoverCard tasks={tasks.slice(3)} onTaskClick={props.onTaskClick} />}
          </IPStack>
        );
      };

      //
      // Render period column Integration
      //
      const renderPeriodColumnIntegration = (
        year: number,
        level: TaskViewScheduleLevel,
        i: number,
        item?: Integration,
      ) => {
        if (!item) return;
        const runs = getRunsForPeriod(year, i + 1, level, item);
        if (runs.length === 0) return null;

        return (
          <IntegrationRunPeriodSummary
            integration={item}
            runs={runs}
            level={level}
            period={i}
            onUpdateRun={props.onUpdateRun}
          />
        );
      };

      //
      // Render period column Audit
      //
      const renderPeriodColumnAudit = (
        year: number,
        level: TaskViewScheduleLevel,
        i: number,
        item?: InternalAuditSchedule,
      ) => {
        if (!item) return;
        const occurrences = getAuditOccurrencesForPeriod(year, i + 1, level, item);
        const calculatedDates = getAuditCalculatedDatesForPeriod(year, i + 1, level, item);
        if (occurrences.length === 0 && calculatedDates.length === 0) return null;

        return (
          <AuditPeriodSummary
            schedule={item}
            occurrences={occurrences}
            calculatedDates={calculatedDates}
            level={level}
            periodStart={getPeriodRange(year, i + 1, level).start}
            onOpenAudit={props.onAuditOccurrenceClick}
          />
        );
      };

      //
      // Generate period columns
      //
      let periodColumns: IColumn[] = [];

      periodColumns = rotateArrayLeft<IColumn>(
        planningRotation,
        Array.from({ length: Math.floor(12 / level) }, (_, i) => {
          const output: IColumn = {
            key: `level ${i + 1}`,
            name: getPeriodColumnName(year, level, i),
            minWidth: level === 1 ? 35 : 70,
            maxWidth: level === 1 ? 35 : 70,
            isMultiline: true,
            isSorted: false,
            isSortedDescending: false,
            onRender: (item?: ItemType, index?: number, column?: IColumn) => {
              if (item instanceof InternalAuditSchedule) {
                return renderPeriodColumnAudit(year, level, i, item);
              } else if (item instanceof Integration) {
                return renderPeriodColumnIntegration(year, level, i, item);
              } else {
                return renderPeriodColumnTask(year, level, i, item);
              }
            },
          };

          return output;
        }),
      );

      //set a fixed key on the first column for sorting identification
      periodColumns[0].key = 'level';
      periodColumns[0].isSorted = sortKey === 'level';
      periodColumns[0].isSortedDescending = sortKey === 'level' ? sortDesc : false;

      //render extra column for the first period in the next year
      periodColumns.push({
        key: 'level 0',
        name: getPeriodColumnName(year + 1, level, planningRotation),
        minWidth: level === 1 ? 60 : 100,
        maxWidth: level === 1 ? 60 : 100,
        isMultiline: true,
        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: ItemType, index?: number, column?: IColumn) => {
          if (item instanceof InternalAuditSchedule) {
            return renderPeriodColumnAudit(year + 1, level, planningRotation, item);
          } else if (item instanceof Integration) {
            return renderPeriodColumnIntegration(year + 1, level, planningRotation, item);
          } else {
            return renderPeriodColumnTask(year + 1, level, planningRotation, item);
          }
        },
      });

      return periodColumns;
    },
    [
      instanceTasks,
      monthsNames,
      planningRotation,
      props.onTaskClick,
      props.onUpdateRun,
      props.onAuditOccurrenceClick,
      sortDesc,
      sortKey,
      t,
    ],
  );

  const [columns, setColumns] = useState<IColumn[]>(
    getColumns(props.filter, props.year, props.level, props.isLoading, sortKey, sortDesc, props.hideContext),
  );

  //
  // Sorting
  //
  const loadSortFromLocalStorage = (): string[] => {
    let sort: string[] = ['level', '1'];

    if (props.cacheKeySort) {
      const storedSort = getLocalStorageData(appContext, props.cacheKeySort);
      if (storedSort) {
        try {
          sort = storedSort.split(',');
        } catch {
          //ignore
        }
      }
    }

    return sort;
  };

  const saveSortToLocalStorage = (key: string, descending: boolean) => {
    if (props.cacheKeySort) {
      const values = [key, descending ? '1' : '0'];
      setLocalStorageData(appContext, props.cacheKeySort, values.join(','));
    }
  };

  const onColumnClick = (ev: React.MouseEvent<HTMLElement> | undefined, column: IColumn | undefined): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    let desc: boolean = false;

    if (currColumn.key.startsWith('level')) {
      desc = !sortDesc;
      let firstCol: string = '';

      for (const newCol of newColumns) {
        if (!firstCol && newCol.key.startsWith('level')) {
          newCol.isSortedDescending = desc;
          newCol.isSorted = true;
          firstCol = newCol.key;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = false;
        }
      }
      setSortKey(firstCol);
    } else {
      for (const newCol of newColumns) {
        if (newCol === currColumn) {
          newCol.isSortedDescending = !newCol.isSortedDescending;
          newCol.isSorted = true;
          desc = newCol.isSortedDescending;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = false;
        }
      }
      setSortKey(currColumn.key);
    }

    //save sort state to state and local storage
    setSortDesc(desc);
    setColumns(newColumns);
    saveSortToLocalStorage(currColumn.key, desc);

    //sort items and save to state
    const newItems = copyAndSort(itemsDisplay, instanceTasks, currColumn.key, desc);
    setItemsDisplay(newItems);

    // notify parent about updated series tasks
    if (props.onUpdateTaskSeries) props.onUpdateTaskSeries(newItems.filter((i) => i instanceof Task) as Task[]);
  };

  const copyAndSort = (
    itemsDisplay: ItemType[],
    instanceTasks: Task[],
    columnKey: string,
    isSortedDescending?: boolean,
  ): ItemType[] => {
    const desc = isSortedDescending ? -1 : 1;

    // Audits have no assignee either, so they sort like integrations do: on a sentinel name that
    // keeps them grouped together, chosen to sort above the integration one.
    if (columnKey === 'assignee') {
      const assigneeName = (item: ItemType): string | undefined => {
        if (item instanceof InternalAuditSchedule) return '---AA';
        if (item instanceof Integration) return '---EC'; //some string that sort consistently above or below user names, to keep integrations grouped together in the sorting

        return appContext.globalDataCache.users.get(item.userId)?.name;
      };

      const secondaryName = (item: ItemType): string | undefined => {
        if (item instanceof InternalAuditSchedule) return item.template.name;
        if (item instanceof Integration) return item.integrationTypeHandler?.name;

        return item.name;
      };

      return itemsDisplay.slice(0).sort((a, b) => {
        if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

        let nameA = assigneeName(a);
        let nameB = assigneeName(b);

        if (nameA === nameB) {
          //When assignee is the same (including both being unassigned), sort on name as secondary sort to have a consistent order
          nameA = secondaryName(a);
          nameB = secondaryName(b);
        }

        return desc * sortOnString(nameA, nameB);
      });
    }

    // on every other column an audit series cannot be compared with a task or an integration: they
    // render as one block on top, sorted on name
    const auditRows = itemsDisplay.filter((i) => i instanceof InternalAuditSchedule) as InternalAuditSchedule[];
    if (auditRows.length > 0) {
      const otherRows = itemsDisplay.filter((i) => !(i instanceof InternalAuditSchedule));
      const sortedOthers = copyAndSort(otherRows, instanceTasks, columnKey, isSortedDescending);
      auditRows.sort((a, b) => desc * sortOnString(a.template.name, b.template.name));

      return [...auditRows, ...sortedOthers];
    }

    // from here on the list is guaranteed to hold tasks and integrations only
    const rows = itemsDisplay as (Task | Integration)[];

    switch (columnKey) {
      case 'name':
        return rows.slice(0).sort((a, b) => {
          if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

          let nameA: string | undefined;
          let nameB: string | undefined;

          if (a instanceof Integration) {
            nameA = a.integrationTypeHandler?.name;
          } else {
            nameA = a.name;
          }
          if (b instanceof Integration) {
            nameB = b.integrationTypeHandler?.name;
          } else {
            nameB = b.name;
          }

          return desc * sortOnString(nameA, nameB);
        });
      case 'tags':
        return rows.slice(0).sort((a, b) => {
          if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

          let taskA: Task | undefined;
          let taskB: Task | undefined;

          if (a instanceof Integration) {
            taskA = undefined;
          } else {
            taskA = a;
          }
          if (b instanceof Integration) {
            taskB = undefined;
          } else {
            taskB = b;
          }

          return desc * sortOnTaskTags(taskA, taskB);
        });
      case 'recurringPattern':
        return rows.slice(0).sort((a, b) => {
          if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

          let patternA: RecurringPattern | undefined;
          let patternB: RecurringPattern | undefined;

          if (a instanceof Integration) {
            patternA = a.recurrencePattern;
          } else {
            patternA = a.recurringPattern;
          }
          if (b instanceof Integration) {
            patternB = b.recurrencePattern;
          } else {
            patternB = b.recurringPattern;
          }

          return desc * sortOnNumber(patternA?.nrOfOccurrencesPerYear(), patternB?.nrOfOccurrencesPerYear());
        });
      case 'context':
        return rows.slice(0).sort((a, b) => {
          if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

          let entityCount1: number = 0;
          let entityCount2: number = 0;

          if (a instanceof Integration) {
            entityCount1 = a.kpi.controlIds?.length || 0;
            entityCount1 += a.kpi.objectiveIds?.length || 0;
          } else {
            entityCount1 = a.riskIds.length;
            entityCount1 += a.controlIds.length;
            entityCount1 += a.processIds.length;
            entityCount1 += a.objectiveIds.length;
            entityCount1 += a.assetIds.length;
          }
          if (b instanceof Integration) {
            entityCount2 = b.kpi.controlIds?.length || 0;
            entityCount2 += b.kpi.objectiveIds?.length || 0;
          } else {
            entityCount2 = b.riskIds.length;
            entityCount2 += b.controlIds.length;
            entityCount2 += b.processIds.length;
            entityCount2 += b.objectiveIds.length;
            entityCount2 += b.assetIds.length;
          }

          return desc * sortOnNumber(entityCount1, entityCount2);
        });
      default:
        if (columnKey.startsWith('level')) {
          return rows.slice(0).sort((a, b) => {
            if (newCopilotTaskOnTopComparator(a, b) !== 0) return newCopilotTaskOnTopComparator(a, b);

            //We cannot compare level columns of Tasks and Integration
            //In this case, order integrations below tasks
            const aIntegration = a instanceof Integration;
            const bIntegration = b instanceof Integration;

            if (aIntegration && !bIntegration) return 1;
            if (!aIntegration && bIntegration) return -1;
            if (aIntegration && bIntegration) return 0;
            if (aIntegration || bIntegration) return 0; //type guard for typescript

            const instancesA = instanceTasks
              .filter((t) => t.taskMasterId === a.taskId && !t.completed)
              .sort((a, b) => desc * sortOnDate(a.startDateTime, b.startDateTime));
            const instancesB = instanceTasks
              .filter((t) => t.taskMasterId === b.taskId && !t.completed)
              .sort((a, b) => desc * sortOnDate(a.startDateTime, b.startDateTime));

            const taskA: Task | undefined = instancesA[0];
            const taskB: Task | undefined = instancesB[0];

            if (taskA && taskB) {
              return desc * sortOnDate(taskA.startDateTime, taskB.startDateTime);
            } else if (!taskA) {
              return 1;
            } else {
              return -1;
            }
          });
        }
        break;
    }

    function newCopilotTaskOnTopComparator(a: ItemType, b: ItemType): number {
      if (props.newCopilotTasks && props.newCopilotTasks.length > 0) {
        const aIsNewCopilotTask = a instanceof Task && props.newCopilotTasks?.some((t) => a.taskId === t.taskId);
        const bIsNewCopilotTask = b instanceof Task && props.newCopilotTasks?.some((t) => b.taskId === t.taskId);
        if (aIsNewCopilotTask && !bIsNewCopilotTask) return -1;
        if (!aIsNewCopilotTask && bIsNewCopilotTask) return 1;
      }

      return 0;
    }

    return rows.slice(0);
  };

  const onRenderRow = (
    rowProps?: IDetailsRowProps,
    defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (defaultRender) {
      if (rowProps?.item instanceof InternalAuditSchedule || rowProps?.item instanceof Integration) {
        return defaultRender({
          ...rowProps,
          styles: {
            root: { backgroundColor: appContext.useDarkMode ? annualPlanRowTintDark : annualPlanRowTintLight },
          },
        });
      }

      if (rowProps?.item instanceof Task && props.newCopilotTasks?.some((t) => t.taskId === rowProps.item.taskId)) {
        return defaultRender({
          ...rowProps,
          styles: {
            root: { backgroundColor: appContext.useDarkMode ? '#3d5a3f58' : '#c1e9c458' },
          },
        });
      }

      return defaultRender(rowProps);
    } else {
      return null;
    }
  };

  //
  // Main render
  //
  if (!props.isLoading && itemsDisplay?.length === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item grow styles={{ root: { position: 'relative' } }}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            enableShimmer={props.isLoading}
            items={itemsDisplay}
            columns={columns}
            selectionMode={props.selectionMode || IPSelectionMode.none}
            selectionPreservedOnEmptyClick={true}
            selection={selection as IPSelection<IObjectWithKey>}
            layoutMode={IPDetailsListLayoutMode.justified}
            constrainMode={ConstrainMode.unconstrained}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            onColumnHeaderClick={onColumnClick}
            onRenderRow={onRenderRow}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};
